import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faYoutube, faFacebook, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons'
import packageJson from '../../../../package.json'

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND = '#6eb446'

const navLink: React.CSSProperties = {
  color: '#94a3b8',
  textDecoration: 'none',
  fontSize: '0.8rem',
  transition: 'color 0.15s',
  whiteSpace: 'nowrap',
}

// ── Social button ─────────────────────────────────────────────────────────────
interface SocialProps {
  href: string
  icon: typeof faYoutube
  label: string
  color: string
}

const SocialLink: React.FC<SocialProps> = ({ href, icon, label, color }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 36,
      borderRadius: '50%',
      backgroundColor: '#1e293b',
      color: '#94a3b8',
      textDecoration: 'none',
      transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
      flexShrink: 0,
    }}
    onMouseEnter={e => {
      e.currentTarget.style.backgroundColor = color
      e.currentTarget.style.color = '#fff'
      e.currentTarget.style.transform = 'translateY(-2px)'
    }}
    onMouseLeave={e => {
      e.currentTarget.style.backgroundColor = '#1e293b'
      e.currentTarget.style.color = '#94a3b8'
      e.currentTarget.style.transform = 'translateY(0)'
    }}
  >
    <FontAwesomeIcon icon={icon} style={{ fontSize: '0.95rem' }} />
  </a>
)

// ── Footer ────────────────────────────────────────────────────────────────────
const Footer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const year = new Date().getFullYear()

  return (
    <footer style={{ backgroundColor: '#0f172a', borderTop: '4px solid #000' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '1.5rem 1.5rem 1.25rem' }}>

        {/* Top row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.25rem',
          paddingBottom: '1.25rem',
          borderBottom: '1px solid #1e293b',
        }}>

          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img
              src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
              width={72}
              alt="Label Dictate"
            />
            <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
              Label Dictate
            </span>
          </div>

          {/* Nav links */}
          <nav style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 0.5rem', alignItems: 'center' }}>
            {[
              { to: '/terms-and-conditions', label: t('footer.terms_and_conditions') },
              { to: '/privacy-policy',       label: t('footer.privacy_policy')       },
              { to: '/about-us',             label: t('footer.about_us')             },
              { to: '/contact-us',           label: t('footer.contact_us')           },
            ].map(({ to, label }, i, arr) => (
              <React.Fragment key={to}>
                <Link
                  to={to}
                  style={navLink}
                  onMouseEnter={e => { e.currentTarget.style.color = BRAND }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8' }}
                >
                  {label}
                </Link>
                {i < arr.length - 1 && (
                  <span style={{ color: '#334155', fontSize: '0.75rem' }}>•</span>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Social icons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <SocialLink href="https://www.youtube.com/@LabelDictate"                      icon={faYoutube}  label="YouTube"   color="#ff0000" />
            <SocialLink href="https://www.facebook.com/profile.php?id=61552169408426"     icon={faFacebook} label="Facebook"  color="#1877f2" />
            <SocialLink href="https://www.instagram.com/labeldictate/"                    icon={faInstagram} label="Instagram" color="#e1306c" />
            <SocialLink href="https://www.tiktok.com/@labeldictate"                       icon={faTiktok}   label="TikTok"    color="#010101" />
          </div>
        </div>

        {/* Bottom row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          paddingTop: '1.25rem',
        }}>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
            © {year} Portal Label Dictate
          </p>

          <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', textAlign: 'center' }}>
            {t('footer.recaptcha')}{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#94a3b8', textDecoration: 'underline' }}
            >
              {t('footer.privacy_google')}
            </a>
            {' '}&{' '}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#94a3b8', textDecoration: 'underline' }}
            >
              {t('footer.terms_google')}
            </a>
            {i18n.language === 'en' ? ' apply.' : ' de Google.'}
          </p>

          <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b' }}>
            v{packageJson.version}
          </p>
        </div>

      </div>
    </footer>
  )
}

export default Footer
